import { privateOfficeContractsService } from "@/services/private-office-contracts-service";
import { parseWithZod } from "@conform-to/zod";
import { formatInTimeZone } from "date-fns-tz";
import { ActionFunctionArgs } from "react-router-dom";
import { z } from "zod";
import { extractErrorMessage } from "@/utils/extract-error-message";
import { startOfMonth } from "date-fns";

export const schema = z
  .object({
    cancellationDate: z.string({
      required_error: "Selecione uma data de cancelamento",
    }),
    noticeStartDate: z.string({
      required_error: "Selecione uma data de início para o aviso prévio",
    }),
    cancellationReason: z.string({
      required_error: "Selecione um motivo",
    }),
    flowType: z.enum(["cancel", "downsell"]),
    otherReason: z.string().optional().nullable(),
    noticeInDays: z.number({
      required_error: "Selecione um período de aviso prévio",
    }),
  })
  .refine(
    (data) => {
      if (data.cancellationReason === "Outros" && !data.otherReason) {
        return false;
      }
      return true;
    },
    {
      message: "Selecione um motivo",
      path: ["otherReason"],
    },
  )
  .superRefine((data, ctx) => {
    const minDate = formatInTimeZone(
      startOfMonth(new Date()),
      "UTC",
      "yyyy-MM-dd",
    );

    if (data.noticeStartDate < minDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_date,
        message:
          "A data de início não pode ser anterior ao início do mês atual",
        path: ["noticeStartDate"],
      });
    }

    if (data.cancellationDate < minDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_date,
        message:
          "A data de cancelamento não pode ser anterior ao início do mês atual",
        path: ["cancellationDate"],
      });
    }
  });

export async function action({ request, params }: ActionFunctionArgs) {
  const { contractId } = params;

  if (!contractId) {
    throw new Error("Contract ID is required");
  }

  const formData = await request.formData();
  const submission = parseWithZod(formData, { schema });

  if (submission.status !== "success") {
    return {
      submission: submission.reply(),
    };
  }

  const { cancellationDate, cancellationReason, otherReason, noticeStartDate } =
    submission.value;

  try {
    const response =
      await privateOfficeContractsService.cancelPrivateOfficeContract({
        id: Number(contractId),
        cancellationDate: formatInTimeZone(
          cancellationDate,
          "UTC",
          "yyyy-MM-dd",
        ),
        cancellationReason: otherReason || cancellationReason,
        noticeStartDate: formatInTimeZone(noticeStartDate, "UTC", "yyyy-MM-dd"),
      });

    return {
      value: submission.reply(),
      status: "success",
      response,
    };
  } catch (error) {
    const errorMessage = extractErrorMessage(
      error,
      "Algo deu errado ao cancelar o contrato",
    );

    return {
      value: submission.reply({
        formErrors: ["cancelationFailed"],
      }),
      status: "error",
      errorMessage,
    };
  }
}

export type Action = Awaited<ReturnType<typeof action>>;
